/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import customPropTypes from 'apps/web_forms/utils/customPropTypes';
import { setCurrentSelectedMultimediaInput } from 'apps/web_forms/slices/multimediaSlice';
import useRules from 'apps/web_forms/hooks/useRules';
import styled from 'styled-components';
import ErrorMessage from 'apps/web_forms/components/commons/ErrorMessage';

import useChangeValue from 'apps/web_forms/hooks/useChangeValue';
import useResourceUpdateHighlight from 'apps/web_forms/hooks/useResourceUpdateHighlight';
import { selectFieldErrorById } from 'apps/web_forms/hooks/useGlobalSelector';
import clsx from 'clsx';

const StyledTextInputContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const TextInput = ({ element, formContext, onChangeValue, toggleActiveValue, showContent }) => {
  const {
    title,
    identifier,
    hint,
    hidden,
    multi_line: isMultiLine,
    validate_msg: validateMsg,
  } = element;
  const { register } = formContext;

  const { changeValue, currentValue } = useChangeValue(
    element,
    onChangeValue,
    formContext
  );

  const errorMessage = useSelector((state) => selectFieldErrorById(state, identifier));
  
  const {
    hasResourceUpdateHighlight,
    removeResourceUpdateHighlight,
  } = useResourceUpdateHighlight({fieldIdentifier: identifier});

  const dispatch = useDispatch();
  const [{ isActive, isElementValid, isRequired, isReadOnly, initialAnswer }] =
    useRules(element, formContext);

  useEffect(() => {
    register(identifier, {
      validate: () => isElementValid || validateMsg || false,
      required: isRequired ? 'This field is required' : false,
    });
  }, [identifier, isElementValid, validateMsg, isRequired]);

  useEffect(() => {
    changeValue(initialAnswer);
  }, [initialAnswer]);

  const onChange = (value) => {
    changeValue(value, true);
  };

  useEffect(() => {
    toggleActiveValue(identifier, isActive);
  }, [isActive]);

  if (!isActive || hidden || !showContent) return null;

  const commonProps = {
    type: 'text',
    name: identifier,
    id: identifier,
    placeholder: 'Answer',
    // value: currentValue ?? '',
    defaultValue: currentValue ?? '',
    onClick: () => dispatch(setCurrentSelectedMultimediaInput({})),
    readOnly: isReadOnly,
    className: clsx(
      errorMessage !== undefined && 'invalid',
      hasResourceUpdateHighlight && 'updateHighlight',
      isReadOnly && 'read-only',
    ),
    onChange: (e) => onChange(e.target.value),
    onFocus: removeResourceUpdateHighlight,
  };

  return (
    <div className="react-form-control">
      <StyledTextInputContainer>
        <label htmlFor={identifier}>{title}</label>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        {isMultiLine ? (
          <textarea {...commonProps} />
        ) : (
          <input {...commonProps} />
        )}
        {hint && <p className="element-hint">{hint}</p>}
        {errorMessage !== undefined && (
          <ErrorMessage id={`${identifier}_errmsg`}>
            {errorMessage}
          </ErrorMessage>
        )}
      </StyledTextInputContainer>
    </div>
  );
};

TextInput.propTypes = {
  element: PropTypes.shape({
    identifier: PropTypes.string,
    title: PropTypes.string,
    initialAnswerExpr: PropTypes.string,
    hint: PropTypes.string,
    validate_msg: PropTypes.string,
    hidden: PropTypes.bool,
    multi_line: PropTypes.bool,
  }).isRequired,
  formContext: customPropTypes.formContext.isRequired,
  onChangeValue: PropTypes.func.isRequired,
};

export default React.memo(TextInput);
